"use client";

import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  DiagramDownload,
  useDiagramRef,
} from "@/components/tools/geometry/diagram";
import {
  type Format,
  formatMeasure,
  formatTableValue,
} from "@/components/tools/geometry/format";
import { NetDiagram } from "@/components/tools/geometry/net-diagrams";
import { SOLID_SYMBOLS } from "@/components/tools/geometry/solid-diagrams";
import {
  SolidFigure,
  SolidInputPanel,
  SolidMetricsTable,
  useSolidInputs,
} from "@/components/tools/geometry/solid-inputs";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type ExactFormat,
  formatExactWithUnit,
  isRationalExact,
} from "@/lib/tool-kit/geometry/exact";
import { answerUnit } from "@/lib/tool-kit/geometry/measurements";
import {
  evaluateSolid,
  type SolidOutcome,
} from "@/lib/tool-kit/geometry/solid-input";
import {
  SOLID_FIELDS,
  type SolidId,
  type SolidResult,
} from "@/lib/tool-kit/geometry/solids";
import {
  conversionRows,
  type LengthUnitId,
  unitSymbol,
} from "@/lib/tool-kit/geometry/units";
import { decimalMarkOf } from "@/lib/tool-kit/math/locale";
import { needsFraction } from "@/lib/tool-kit/math/rational";

/** English strings. A translated content file overrides these through `ui`. */
export const SURFACE_AREA_CALCULATOR_STRINGS = {
  solidLabel: "Solid",
  solids: {
    cube: "Cube",
    box: "Rectangular prism (box)",
    triPrism: "Triangular prism",
    cylinder: "Cylinder",
    cone: "Cone",
    sphere: "Sphere",
    hemisphere: "Hemisphere",
    pyramid: "Pyramid (rectangular base)",
    frustum: "Cone frustum (truncated cone)",
    ellipsoid: "Ellipsoid",
  },
  fields: {
    side: "Edge length (a)",
    length: "Length (l)",
    width: "Width (w)",
    height: "Height (h)",
    radius: "Radius (r)",
    sideA: "Triangle side a",
    sideB: "Triangle side b",
    sideC: "Triangle side c",
    prismLength: "Prism length (L)",
    bottomRadius: "Bottom radius (R)",
    topRadius: "Top radius (r)",
    semiA: "Semi-axis a",
    semiB: "Semi-axis b",
    semiC: "Semi-axis c",
  },
  unitLabel: "Unit of length",
  mixLabel: "Measurements are in different units",
  resultUnitLabel: "Give the answer in",
  decimalsLabel: "Decimal places",
  unitAria: "{field}: unit",
  lengthUnits: {
    mm: "Millimeters (mm)",
    cm: "Centimeters (cm)",
    m: "Meters (m)",
    km: "Kilometers (km)",
    in: "Inches (in)",
    ft: "Feet (ft)",
    yd: "Yards (yd)",
    mi: "Miles (mi)",
  },
  results: "Results",
  total: "Total surface area",
  totalDecimal: "Total as a decimal",
  totalApprox: "Total surface area (approximate)",
  lateral: {
    cube: "Lateral area (four side faces)",
    box: "Lateral area (four side faces)",
    triPrism: "Lateral area (three rectangles)",
    cylinder: "Lateral (curved) area",
    cone: "Lateral (curved) area",
    sphere: "Lateral area",
    hemisphere: "Curved surface area",
    pyramid: "Lateral area (four triangles)",
    frustum: "Lateral (curved) area",
    ellipsoid: "Lateral area",
  },
  approximateNote:
    "A general ellipsoid has no closed-form surface area. This is Knud Thomsen's approximation, which is within about 1% of the true value and exact for a sphere.",
  moreHeading: "Other measurements",
  measurementHeader: "Measurement",
  exactHeader: "Exact",
  decimalHeader: "Decimal",
  metrics: {
    baseArea: "Base area",
    topArea: "Top area",
    slantHeight: "Slant height",
    slantOnLength: "Slant height of the faces on the length sides (s₁)",
    slantOnWidth: "Slant height of the faces on the width sides (s₂)",
    spaceDiagonal: "Space diagonal",
    faceDiagonal: "Face diagonal",
    crossSection: "Great-circle cross-section area",
  },
  workingHeading: "The working",
  netHeading: "The net",
  netTitle: "Net of a {solid}, with the area of each face",
  netNote:
    "Unfold the solid and the surface lies flat: add up the areas of the pieces to get the total.",
  noNet:
    "This solid has no flat net. Its surface curves in two directions, so it cannot be unrolled without stretching — the formula comes from calculus, or for the sphere from Archimedes' result that its area is four times that of its great circle.",
  downloadNet: "Download net (PNG)",
  figureHeading: "Figure",
  figureTitle: "{solid} with its measurements labeled",
  downloadFigure: "Download figure (PNG)",
  slantLabel: "l = {value}",
  slantOnLengthLegend: "s₁ = {value}",
  slantOnWidthLegend: "s₂ = {value}",
  unitsHeading: "The same area in other units",
  unitHeader: "Unit",
  areaHeader: "Area",
  copyResult: "Copy result",
  copied: "Copied!",
  summary: "{solid}: total surface area = {exact} ≈ {decimal}",
  summaryExact: "{solid}: total surface area = {exact}",
  summaryApprox: "{solid}: total surface area ≈ {decimal}",
  enter: "Enter every measurement to see the surface area.",
  invalid:
    "Enter each measurement as a number — decimals and fractions such as 3/4 work.",
  positive: "Every measurement must be greater than zero.",
  triangle:
    "These three sides cannot make a triangle: the longest side must be shorter than the other two added together.",
} as const;

type Strings = typeof SURFACE_AREA_CALCULATOR_STRINGS;
type Solved = Extract<SolidOutcome, { ok: true }>;

const ID_PREFIX = "sa";

function needsDecimal(result: SolidResult, format: ExactFormat): boolean {
  if (result.total === null) {
    return false;
  }
  if (!isRationalExact(result.total)) {
    return true;
  }
  return result.total.some((term) =>
    format.decimals ? needsFraction(term.coef) : term.coef.den !== BigInt(1)
  );
}

function metricValue(result: SolidResult, key: string): number | undefined {
  return result.metrics.find((m) => m.key === key)?.value;
}

function legendFor(
  solid: SolidId,
  solved: Solved,
  strings: Strings,
  lengthText: (value: number) => string
): string[] {
  const { read, result } = solved;
  const items: string[] = SOLID_FIELDS[solid].map(
    (id) => `${SOLID_SYMBOLS[id]} = ${read.labels[id]}`
  );
  const cone = metricValue(result, "slantHeight");
  const onLength = metricValue(result, "slantOnLength");
  const onWidth = metricValue(result, "slantOnWidth");
  if (cone !== undefined) {
    items.push(t(strings.slantLabel, { value: lengthText(cone) }));
  }
  if (onLength !== undefined) {
    items.push(t(strings.slantOnLengthLegend, { value: lengthText(onLength) }));
  }
  if (onWidth !== undefined) {
    items.push(t(strings.slantOnWidthLegend, { value: lengthText(onWidth) }));
  }
  return items;
}

function Net({
  solid,
  solved,
  strings,
  lengthText,
}: {
  solid: SolidId;
  solved: Solved;
  strings: Strings;
  lengthText: (value: number) => string;
}) {
  const svgRef = useDiagramRef();
  const { result } = solved;
  if (result.net === "none") {
    return <p className="text-muted-foreground text-sm">{strings.noNet}</p>;
  }
  return (
    <div className="space-y-2">
      <p className="text-muted-foreground text-sm">{strings.netNote}</p>
      <NetDiagram
        legend={legendFor(solid, solved, strings, lengthText)}
        net={result.net}
        slant={{
          cone: metricValue(result, "slantHeight"),
          onLength: metricValue(result, "slantOnLength"),
          onWidth: metricValue(result, "slantOnWidth"),
        }}
        svgRef={svgRef}
        title={t(strings.netTitle, { solid: strings.solids[solid] })}
        values={solved.read.numbers}
      />
      <DiagramDownload
        fileName={`net-${solid}`}
        label={strings.downloadNet}
        svgRef={svgRef}
      />
    </div>
  );
}

function summaryText(
  strings: Strings,
  solid: SolidId,
  result: SolidResult,
  exact: string,
  decimal: string,
  showDecimal: boolean
): string {
  let template: string = showDecimal ? strings.summary : strings.summaryExact;
  if (result.total === null) {
    template = strings.summaryApprox;
  }
  return t(template, { solid: strings.solids[solid], exact, decimal });
}

function Results({
  solid,
  solved,
  strings,
  unit,
  places,
  fmt,
}: {
  solid: SolidId;
  solved: Solved;
  strings: Strings;
  unit: LengthUnitId;
  places: number;
  fmt: Format;
}) {
  const { result } = solved;
  const exactFormat: ExactFormat = {
    decimals: solved.format.style === "decimal",
    mark: solved.format.mark,
  };
  const areaUnit = unitSymbol(unit, 2);
  const lengthText = (value: number) =>
    `${formatMeasure(fmt, value, places)} ${unitSymbol(unit, 1)}`;
  const decimal = `${formatMeasure(fmt, result.totalValue, places)} ${areaUnit}`;
  const exact = result.total
    ? formatExactWithUnit(result.total, areaUnit, exactFormat)
    : decimal;
  const showDecimal = needsDecimal(result, exactFormat);
  const rows = conversionRows(result.totalValue, unit, 2).map((row) => [
    row.symbol,
    formatTableValue(fmt, row.value),
  ]);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={result.approximate ? strings.totalApprox : strings.total}
          value={result.approximate ? `≈ ${decimal}` : exact}
        />
        {showDecimal ? (
          <StatCard label={strings.totalDecimal} value={decimal} />
        ) : null}
        {result.lateral && result.lateralValue !== null ? (
          <StatCard
            label={strings.lateral[solid]}
            value={formatExactWithUnit(result.lateral, areaUnit, exactFormat)}
          />
        ) : null}
      </StatGrid>
      {result.approximate ? <Notice>{strings.approximateNote}</Notice> : null}
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={summaryText(strings, solid, result, exact, decimal, showDecimal)}
      />
      {result.metrics.length > 0 ? (
        <>
          <h3 className={WIDGET_H3}>{strings.moreHeading}</h3>
          <SolidMetricsTable
            fmt={fmt}
            format={exactFormat}
            headers={{
              measurement: strings.measurementHeader,
              exact: strings.exactHeader,
              decimal: strings.decimalHeader,
            }}
            labels={strings.metrics}
            metrics={result.metrics}
            places={places}
            unit={unit}
          />
        </>
      ) : null}
      {result.areaSteps.length > 0 || result.areaFormulas.length > 0 ? (
        <>
          <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
          <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
            {result.areaFormulas.map((formula) => (
              <p key={formula}>{formula}</p>
            ))}
            {result.areaSteps.map((step) => (
              <p key={step}>{step}</p>
            ))}
          </div>
        </>
      ) : null}
      <h3 className={WIDGET_H3}>{strings.netHeading}</h3>
      <Net
        lengthText={lengthText}
        solid={solid}
        solved={solved}
        strings={strings}
      />
      <h3 className={WIDGET_H3}>{strings.figureHeading}</h3>
      <SolidFigure
        fileName={`surface-area-${solid}`}
        outcome={solved}
        slant={
          metricValue(result, "slantHeight") === undefined
            ? undefined
            : t(strings.slantLabel, {
                value: lengthText(metricValue(result, "slantHeight") ?? 0),
              })
        }
        solid={solid}
        strings={strings}
      />
      <h3 className={WIDGET_H3}>{strings.unitsHeading}</h3>
      <DataTable head={[strings.unitHeader, strings.areaHeader]} rows={rows} />
    </>
  );
}

function errorMessage(strings: Strings, error: string): string {
  const messages: Record<string, string> = {
    invalid: strings.invalid,
    positive: strings.positive,
    triangle: strings.triangle,
  };
  return messages[error] ?? strings.invalid;
}

export default function SurfaceAreaCalculatorWidget() {
  const strings = useToolUi(SURFACE_AREA_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const mark = decimalMarkOf(fmt);
  const state = useSolidInputs("cylinder");
  const outcome = evaluateSolid(state.solid, state.values, state.setup, mark);
  let body: React.ReactNode;
  if (outcome.ok) {
    body = (
      <Results
        fmt={fmt}
        places={Number(state.places)}
        solid={state.solid}
        solved={outcome}
        strings={strings}
        unit={answerUnit(state.setup)}
      />
    );
  } else if (outcome.error === "enter") {
    body = <Notice>{strings.enter}</Notice>;
  } else {
    body = <ErrorNotice>{errorMessage(strings, outcome.error)}</ErrorNotice>;
  }
  return (
    <>
      <SolidInputPanel idPrefix={ID_PREFIX} state={state} strings={strings} />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
    </>
  );
}
